import moment from "moment";
import { getDateLabel } from "src/app/methods/getDateLabel";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { IProject } from "src/redux/project/types";
import { IntlShape } from "react-intl";
import { ITask } from "src/redux/task/taskReducer";
import { IMember } from "src/app/project/ProjectTypes";
import { Row } from "src/redux/dictionary/types";
import { toPercents } from "src/utils/methods";
import { getCountedSelectValues } from "../AggregatedSelect/components/AggregatedLabels/AggregatedLabels";

export interface VirtuosoMetaValue {
  index: number;
  values: (string | null)[];
}

export const getMetaValues = (
  tasks: ITask[],
  visibleColumnIds: string[],
  membersList: IMember[],
  dictionaryAutoCompletes: { [key: string]: Row[] },
  intl: IntlShape,
  projectId: string | null,
  projectTasksAutocomplete: {
    [projectId: string]: ITask[];
  },
  wsProjectAutocompleteList: IProject[],
) => {
  const metaValues: VirtuosoMetaValue[] = [];

  tasks.forEach((task, index: number) => {
    const titleField = task.metadata.find((item) => item.key === "title");
    const metadata = task.metadata
      .filter(
        (column) =>
          visibleColumnIds.includes(column.uuid) &&
          column.key !== "title" &&
          column.key !== "owner",
      )
      .sort((a, b) => a.rank?.localeCompare(b.rank));

    if (titleField) {
      metadata.unshift(titleField);
    }

    metaValues.push({
      index,
      values: metadata.map((field) => {
        const options = field.data?.singleSelectOptions;

        if (!field.value) return null;

        switch (field.type) {
          case MetaFieldType.Number: {
            return field.value;
          }

          case MetaFieldType.SingleSelect: {
            const value = options?.find(
              (item) => item?.value === field?.value,
            )?.name;

            return value;
          }

          case MetaFieldType.MultiSelect: {
            const value = field.value
              ?.map(
                (v: string) =>
                  options?.find((option) => option.value === v)?.name,
              )
              .join(" ");

            return value;
          }

          case MetaFieldType.AggregatedSelect: {
            const multiValue =
              options?.filter((item) => field.value?.includes(item.value)) ||
              [];
            return getCountedSelectValues(multiValue)
              .map(
                (item: { name: string; count: number }) =>
                  `${item.name} x ${item.count}`,
              )
              .join(", ");
          }

          case MetaFieldType.Member: {
            const assignee = membersList?.find(
              (item) => item.id === field.value,
            );

            if (assignee) return `@${assignee?.name}`;
            break;
          }

          case MetaFieldType.DictionaryElement: {
            const wsDictionaryUuid = field.data?.wsDictionaryUuid;
            const list =
              dictionaryAutoCompletes[`dictionaryType_${wsDictionaryUuid}`] ||
              [];

            const value = list.find((item: { uuid: string }) => {
              return item.uuid === field.value;
            })?.title;

            return value;
          }

          case MetaFieldType.File: {
            return field.value
              ?.map((item: { name: string }) => item.name)
              .join(" ");
          }

          case MetaFieldType.Creator:
          case MetaFieldType.Content:
          case MetaFieldType.PublicationRelated: {
            if (!projectId) return null;

            const element = (projectTasksAutocomplete[projectId] ?? []).find(
              (item: { taskId: string }) => item.taskId === field.value,
            );

            return element?.title;
          }

          case MetaFieldType.Project: {
            const selectedWsProject = wsProjectAutocompleteList.find(
              (project: IProject) => project.projectId === field.value,
            );

            return selectedWsProject?.projectName;
          }

          case MetaFieldType.Date: {
            const date = field.value ? moment(field.value.date) : null;

            const label = getDateLabel({
              lang: "en-EN",
              date,
              disableRedLabel: false,
              intl,
              returnRawString: true,
            });

            return label;
          }

          case MetaFieldType.Percent: {
            return `${toPercents(field.value)}%`;
          }

          case MetaFieldType.Currency: {
            if (!field.value) return null;

            const { currencyCode } = field.data;

            return `${currencyCode} ${field.value}`;
          }
        }

        return field.value;
      }),
    });
  });

  return metaValues;
};
